import { useState, useEffect, useRef, useCallback } from 'react';
import { APP, getFileChipBadgeColor } from './tokens';

/** A selected text snippet from the document viewer */
export interface SelectedSnippetChip {
  text: string;
  page?: number;
  paragraphIndex?: number;
}

interface InputBarProps {
  onSend: (message: string) => void;
  onFileDrop?: (files: File[]) => void;
  attachedFiles?: File[];
  onRemoveFile?: (index: number) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Pre-fill the input text (e.g. from quick action clicks). Cleared after consumption. */
  prefill?: string;
  onPrefillConsumed?: () => void;
  /** When provided and disabled=true, shows a Stop button that calls this handler. */
  onStop?: () => void;
  /** Selected text snippet from the document viewer */
  selectedSnippet?: SelectedSnippetChip | null;
  /** Remove the selected snippet */
  onRemoveSnippet?: () => void;
  /** Optional toolbar controls next to the attach button (e.g. Projects picker). */
  destinationSlot?: import('react').ReactNode;
}

function getFileBadgeLabel(file: File): string {
  const extension = file.name.split('.').pop()?.trim();
  if (extension) return extension.toUpperCase();
  if (file.type.startsWith('image/')) return 'IMG';
  if (file.type.startsWith('audio/')) return 'MP3';
  return 'FILE';
}

function AttachedFileChip({
  file,
  disabled = false,
  onRemove,
}: {
  file: File;
  disabled?: boolean;
  onRemove?: () => void;
}) {
  const badge = getFileBadgeLabel(file);
  const badgeBg = getFileChipBadgeColor(badge);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: '100%',
        borderRadius: 6,
        paddingBlock: 4,
        paddingInline: 8,
        gap: 6,
        backgroundColor: APP.bgCardBody,
        border: `1px solid ${APP.border}`,
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          display: 'inline-block',
          borderRadius: 3,
          paddingBlock: 1,
          paddingInline: 4,
          backgroundColor: badgeBg,
          fontSize: 10,
          fontWeight: 700,
          color: '#FFFFFF',
          fontFamily: APP.fontFamily,
          lineHeight: '12px',
        }}
      >
        {badge}
      </span>
      <span
        title={file.name}
        style={{
          minWidth: 0,
          fontSize: 11,
          fontWeight: 400,
          color: APP.textPrimary,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {file.name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${file.name}`}
        style={{
          background: 'none',
          border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          padding: 0,
          marginLeft: 'auto',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
          <path d="M2 2l6 6M8 2l-6 6" stroke={APP.textFileBadgeDismiss} strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

// ─── Selected Snippet Chip ──────────────────────────

export function SnippetChip({
  snippet,
  onRemove,
  disabled = false,
}: {
  snippet: SelectedSnippetChip;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  const truncated = snippet.text.length > 60 ? snippet.text.slice(0, 57) + '…' : snippet.text;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: '100%',
        borderRadius: 6,
        paddingBlock: 4,
        paddingInline: 8,
        gap: 6,
        backgroundColor: '#FFF8F0',
        border: '1px solid #E8C9A8',
        boxSizing: 'border-box',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" fill="#D97645" />
      </svg>
      <span
        title={snippet.text}
        style={{
          minWidth: 0,
          fontSize: 11,
          fontWeight: 500,
          color: '#9E4B1C',
          fontFamily: APP.fontFamily,
          lineHeight: '14px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        &ldquo;{truncated}&rdquo;{snippet.page ? ` (p.${snippet.page})` : ''}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove selection"
          style={{
            background: 'none',
            border: 'none',
            cursor: disabled ? 'default' : 'pointer',
            padding: 0,
            marginLeft: 'auto',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
            <path d="M2 2l6 6M8 2l-6 6" stroke="#9E4B1C" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── Main InputBar ─────────────────────────────────

export default function InputBar({
  onSend,
  onFileDrop,
  attachedFiles = [],
  onRemoveFile,
  placeholder = 'Ask anything or drop a file\u2026',
  disabled = false,
  prefill,
  onPrefillConsumed,
  onStop,
  selectedSnippet,
  onRemoveSnippet,
  destinationSlot,
}: InputBarProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSend =
    (!!value.trim()
      || attachedFiles.length > 0
      || !!selectedSnippet)
    && !disabled;
  const isExpanded = value.trim().length > 0 || attachedFiles.length > 0 || !!selectedSnippet;
  const effectivePlaceholder = disabled
    ? 'Working on it\u2026'
    : selectedSnippet
      ? 'Ask about this selection\u2026'
      : attachedFiles.length === 1
          ? `${attachedFiles[0].name} ready \u2014 ask anything\u2026`
          : attachedFiles.length > 1
            ? `${attachedFiles.length} files ready \u2014 ask anything\u2026`
            : placeholder;

  // Apply prefill when it changes
  useEffect(() => {
    if (prefill) {
      setValue(prefill);
      onPrefillConsumed?.();
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [prefill, onPrefillConsumed]);

  useEffect(() => {
    if (selectedSnippet?.text) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [selectedSnippet?.text]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed && attachedFiles.length === 0 && !selectedSnippet) return;
    if (disabled) return;
    onSend(trimmed);
    setValue('');
  }, [
    value,
    disabled,
    onSend,
    attachedFiles.length,
    selectedSnippet,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onFileDrop) {
      onFileDrop([...attachedFiles, ...Array.from(files)]);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    e.target.value = '';
  };

  return (
    <div
      style={{
        paddingTop: 64,
        paddingRight: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        // No top border — composer floats free of the viewport edge.
        boxSizing: 'border-box',
        // Cap composer width and center it so the input doesn't span
        // the full viewport on wide screens.
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 760,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 12,
          minHeight: 72,
          padding: '18px 16px',
          gap: isExpanded ? 12 : 0,
          backgroundColor: APP.bgInputBar,
          border: `1px solid ${APP.border}`,
          boxShadow: APP.inputShadow,
          boxSizing: 'border-box',
          transition: 'border-radius 180ms ease, padding 180ms ease, gap 180ms ease',
          position: 'relative',
        }}
      >
        {(attachedFiles.length > 0 || selectedSnippet) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {selectedSnippet && (
              <SnippetChip snippet={selectedSnippet} onRemove={onRemoveSnippet} disabled={disabled} />
            )}
            {attachedFiles.map((file, index) => (
              <AttachedFileChip
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                disabled={disabled}
                onRemove={() => onRemoveFile?.(index)}
              />
            ))}
          </div>
        )}

        {/* Text input — full width, top of the composer */}
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            // Auto-resize: reset height then set to scrollHeight
            const el = e.target;
            el.style.height = '0';
            el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
          }}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          disabled={disabled}
          rows={1}
          style={{
            width: '100%',
            fontSize: 13,
            fontFamily: APP.fontFamily,
            color: APP.textPrimary,
            lineHeight: '20px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            padding: 0,
            resize: 'none',
            overflow: 'hidden',
            minHeight: 20,
            maxHeight: 120,
          }}
        />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* Bottom toolbar: attach + pills on left, send on right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {/* Paperclip / attach */}
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={disabled}
            aria-label="Attach files"
            style={{
              width: 26,
              height: 26,
              background: 'none',
              border: `1px solid ${attachedFiles.length > 0 ? APP.borderChatInputAccent : APP.border}`,
              borderRadius: 999,
              cursor: disabled ? 'default' : 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={attachedFiles.length > 0 ? APP.borderChatInputAccent : APP.iconDefault} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>

          {destinationSlot}

          <div style={{ flexGrow: 1 }} />

          {/* Send / Stop button */}
          {disabled && onStop ? (
            <button
              type="button"
              onClick={onStop}
              aria-label="Stop processing"
              style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 999,
                backgroundColor: '#D94545',
                border: '1px solid #D94545',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
                <rect width="10" height="10" rx="1.5" fill="#FFFFFF" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSend}
              aria-label="Send message"
              style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 999,
                backgroundColor: canSend ? APP.bgChatSendBtn : 'transparent',
                border: `1px solid ${canSend ? APP.bgChatSendBtn : APP.border}`,
                cursor: canSend ? 'pointer' : 'default',
                flexShrink: 0,
                opacity: canSend ? 1 : 0.55,
                transition: 'background-color 160ms ease, border-color 160ms ease, opacity 160ms ease',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <path
                  d="M2 7h10M8 4l3 3-3 3"
                  stroke={canSend ? '#FDFCFB' : APP.iconDefault}
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
